import {Box, Typography} from '@mui/material';
import './InvestigateChatPanel.scss';

import React, {
  FC,
  useEffect,
  useState,
  KeyboardEvent,
  useId,
  useCallback,
} from 'react';
import ArrButton from '../../../common/arr-button/ArrButton';

import {ReactComponent as DownloadIcon} from '../../../../assets/images/download-base-icon.svg';
import ArrTextArea from '../../../common/arr-text-area/ArrTextArea';

import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {t} from 'i18next';
import QuestionAndAnswerPanel from './QuestionAndAnswerPanel/QuestionAndAnswerPanel';
import ErrorBanner from './QuestionAndAnswerPanel/error-banner/ErrorBanner';
import TranscriptHeaderAction from '../common/TranscriptHeaderAction';
import {useConversation} from './useConversation';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useDownloadConversation} from '../../../../hooks/ai/investigate/useInvestigate';
import {ToastType} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ReactComponent as FolderIcon} from '../../../../assets/images/no-data-found.svg';
import {ICreateConversationResponse} from '../../../../api/types/aiInvestigate.type';

export interface TranscriptDetails {
  transcriptId: number;
  isOwner: boolean;
  ownerName: string;
  transcriptName: string;
  indexName: string;
  indexId: number;
  isNew: boolean;
  documentCount?: number;
}

type InvestigateChatPanelProps = {
  transcriptDetails: TranscriptDetails;
  isTranscriptPanelCollapsed: boolean;
  isReadOnly?: boolean;
  setIsTranscriptPanelCollapsed: (isCollapsed: boolean) => void;
  setSelectedTranscriptDetails: React.Dispatch<
    React.SetStateAction<TranscriptDetails>
  >;
  disableNewChat: boolean;
  onNewChatClick: () => void;
  onConversationUpdate?: (response: ICreateConversationResponse) => void;
};
export interface ConversationDto {
  conversationId: string;
  conversationNumber: number;
  question: string;
  answer: string;
  suggestedQuestions: {
    id: number;
    question: string;
  }[];
  createDate: string;
  updateDate: string;
  isRemoved: boolean;
  isError?: boolean;
  isStreaming: boolean;
}
const MAX_PROMPT_LENGTH = 2000;
const COLLAPSED_PROMPT_MAX_WIDTH = 74.8; // in percent
const InvestigateChatPanel: FC<InvestigateChatPanelProps> = ({
  transcriptDetails,
  isTranscriptPanelCollapsed = false,
  isReadOnly = false,
  setIsTranscriptPanelCollapsed,
  setSelectedTranscriptDetails,
  disableNewChat,
  onNewChatClick,
  onConversationUpdate,
}) => {
  const [promptText, setPromptText] = useState('');
  const descId = useId();

  const [lastSubmittedPromptText, setLastSubmittedPromptText] = useState('');
  const {
    conversations,
    askQuestion,
    setConversations,
    loading,
    isError,
    isStreaming,
    isTranscriptLoading,
    isInactiveIndex,
    deleteConversation,
  } = useConversation({transcriptDetails, onConversationUpdate});
  useEffect(() => {
    if (isError) {
      setPromptText(lastSubmittedPromptText);
    }
  }, [isError]);

  const {mutate: downloadConversation} = useDownloadConversation();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);

  const promptTextChangeHandler = (promptMessage: string) => {
    if (promptMessage.trim().length <= MAX_PROMPT_LENGTH) {
      setPromptText(promptMessage);
    }
  };
  const promptRunHandler = () => {
    if (!promptText.trim() || isInactiveIndex) return;
    if (transcriptDetails?.isNew) {
      setSelectedTranscriptDetails((prev) => ({
        ...prev,
        isNew: false,
      }));
    }
    setLastSubmittedPromptText(promptText);
    setPromptText('');
    askQuestion(promptText);
  };

  const promptKeyDownHandler = (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (promptText.trim().length > 0 && !loading && !isInactiveIndex) {
        promptRunHandler();
      }
    }
  };
  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const onSuggestedPromptSelect = (question: string) => {
    if (isInactiveIndex) return;
    setLastSubmittedPromptText(question);
    setPromptText('');
    askQuestion(question);
  };

  const onDownloadBtnClick = useCallback(
    (transcriptId: number) => {
      downloadConversation(
        {transcriptId},
        {
          onSuccess: () => {
            triggerToast(
              t('ai.investigate.chatPanel.successDownloadMessage'),
              ToastType.Success
            );
          },
          onError: () => {
            triggerToast(
              t('ai.investigate.chatPanel.ErrorMessage'),
              ToastType.Error
            );
          },
        }
      );
    },
    [downloadConversation]
  );

  useEffect(() => {
    setPromptText('');
  }, [transcriptDetails]);

  const topPanel = (
    <Box className="chat-top-panel">
      <Box className="left-content">
        {isTranscriptPanelCollapsed && (
          <>
            <TranscriptHeaderAction
              onCollapseClick={() => setIsTranscriptPanelCollapsed(false)}
              disableNewChat={disableNewChat}
              onNewChatClick={onNewChatClick}
            />
            <Typography
              className="transcript-name typo-text label3_size"
              data-testid="investigate-chat-panel-transcript-name"
            >
              {transcriptDetails?.transcriptName}
            </Typography>
          </>
        )}
      </Box>

      <Box className="right-content">
        {(conversations.length > 0 || !transcriptDetails?.isOwner) && (
          <ArrButton
            dataTestId="download-chat-btn"
            className="download-button"
            size="xs"
            onClick={() => onDownloadBtnClick(transcriptDetails?.transcriptId)}
            variant="secondary"
            endIcon={<DownloadIcon />}
            aria-label={t('ai.investigate.chatPanel.download')}
            disabled={conversations.length == 0}
          >
            {t('ai.investigate.chatPanel.download')}
          </ArrButton>
        )}
      </Box>
    </Box>
  );
  const promptPanel = !isReadOnly && (
    <Box className="chat-prompt-panel">
      <Box
        className={`chat-prompt-component-container`}
        sx={{
          width: isTranscriptPanelCollapsed
            ? `${COLLAPSED_PROMPT_MAX_WIDTH}%`
            : '100%',
        }}
      >
        <Box className="search-index-panel">
          <Typography
            className="search-index-label typo-text subhead5_size"
            data-testid="search-index-label"
          >
            {t('ai.investigate.chatPanel.searchIndex')}
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <InfoIcon
                className="search-index-info-icon"
                data-testid="search-index-toggle-info-tooltip"
              />
            }
            placement="right"
            forceInLineToolTip
          >
            {t('ai.investigate.chatPanel.searchIndexTT')}
          </ArrTooltipWrapper>
          <Typography
            className="search-index-label-separator typo-text subhead5_size"
            data-testid="investigate-chat-panel-separator"
          >
            :
          </Typography>

          <Typography
            className="search-index-name typo-text subhead5_size"
            data-testid="search-index-name"
          >
            {transcriptDetails?.indexName}
            {transcriptDetails?.indexId > 0 && (
              <>
                {' '}
                ({transcriptDetails.documentCount ?? 0}{' '}
                {t('ai.investigate.listItem.documents')})
              </>
            )}
          </Typography>
        </Box>
        <Box className="prompt-input-panel" data-testid="prompt-input-panel">
          <Box display="flex" width="100%" gap={1}>
            <ArrTextArea
              dataTestId="prompt-input"
              className="prompt-text-field"
              onChange={promptTextChangeHandler}
              onKeyDown={promptKeyDownHandler}
              value={promptText}
              maxRows={2}
              rows={2}
              placeholder={t('ai.investigate.chatPanel.askQuestion')}
              maxLength={MAX_PROMPT_LENGTH}
              disabled={isInactiveIndex || transcriptDetails?.indexId <= 0}
            />
            <ArrButton
              dataTestId="run-button"
              size="small"
              className="prompt-run-button"
              aria-label={t('ai.investigate.chatPanel.run')}
              aria-describedby={descId}
              disabled={
                promptText.trim().length === 0 ||
                loading ||
                isInactiveIndex ||
                isTranscriptLoading ||
                isStreaming
              }
              onClick={promptRunHandler}
            >
              {t('ai.investigate.chatPanel.run')}
            </ArrButton>
            <Typography
              id={descId}
              component="p"
              className="sr-only"
              data-testid="investigate-chat-panel-run-hint"
            >
              {t('ai.investigate.chatPanel.runHint')}
            </Typography>
          </Box>
          <Typography
            className={`promp-validation-text typo-text body5_size ${promptText.length === MAX_PROMPT_LENGTH ? 'error' : ''}`}
            data-testid="txt-char-limit"
          >
            {`${promptText.length}/${MAX_PROMPT_LENGTH}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const aiChatWelcomeTextPanel = (
    <Box
      data-testid="ai-chat-welcome-text-panel"
      className="ai-chat-welcome-text-panel"
    >
      <Box
        sx={{
          width: isTranscriptPanelCollapsed
            ? `${COLLAPSED_PROMPT_MAX_WIDTH}%`
            : '100%',
        }}
      >
        <Typography
          className="ai-chat-welcome-text typo-text subhead2_size first-text"
          data-testid="investigate-chat-panel-first-welcome-text"
        >
          {t('ai.investigate.chatPanel.firstWelcomeText')}
        </Typography>
        <Typography
          className="ai-chat-welcome-text typo-text subhead2_size second-text"
          data-testid="investigate-chat-panel-second-welcome-text"
        >
          {t('ai.investigate.chatPanel.secondWelcomeText')}
        </Typography>
      </Box>
    </Box>
  );

  const EmptyTranscriptOverlay: React.FC = () => {
    return (
      <Box
        className="empty-transcript-overlay"
        data-testid="empty-transcript-overlay"
      >
        <Box className="icon-text-container" data-testid="icon-text-container">
          <FolderIcon className="folder-icon" />
          <Typography
            className="typo-text headline3_size"
            data-testid="empty-transcript-overlay-text"
          >
            {t('ai.investigate.questionAndAnswerPanel.emptyTranscript')}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="investigate-chat-panel">
      {(conversations.length > 0 ||
        isTranscriptPanelCollapsed ||
        !transcriptDetails.isOwner) &&
        topPanel}
      {isInactiveIndex && transcriptDetails.isOwner && !isTranscriptLoading && (
        <ErrorBanner
          className="inactive-index-error-banner"
          message={t('ai.investigate.chatPanel.inactiveIndex')}
        />
      )}

      {conversations.length || loading || isTranscriptLoading ? (
        <Box className="question-and-answer-panel">
          {isTranscriptLoading ? (
            <Box className="transcript-switch-spinner-container">
              <ArrLoadingSpinner />
            </Box>
          ) : (
            <QuestionAndAnswerPanel
              conversations={conversations}
              setConversations={setConversations}
              transcriptId={transcriptDetails.transcriptId}
              indexId={transcriptDetails.indexId}
              loading={loading}
              isTranscriptPanelCollapsed={isTranscriptPanelCollapsed}
              readOnly={!transcriptDetails.isOwner}
              transcriptOwnerName={transcriptDetails.ownerName}
              onSuggestedPromptSelect={onSuggestedPromptSelect}
              isStreaming={isStreaming}
              onDeleteConversation={deleteConversation}
              isTranscriptLoading={isTranscriptLoading}
            />
          )}
        </Box>
      ) : transcriptDetails.isOwner ? (
        aiChatWelcomeTextPanel
      ) : (
        <EmptyTranscriptOverlay />
      )}
      {transcriptDetails.isOwner && promptPanel}
      {showToast && (
        <ArrToastNotification
          data-testid="tag-toast"
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default InvestigateChatPanel;
